import React, { useMemo } from 'react';
import { GenericFormPage } from '../../components/generic';
import { useInvoicePayment, InvoicePayment } from './useData';
import { moduleConfig, getInitialFormData, mapItemToFormData } from './config';

type FormInvoicePayment = Omit<InvoicePayment, 'invoiceId' | 'clientId'> & {
  invoiceId?: string | number;
  clientId?: string | number;
};

const Form: React.FC = () => {
  const configWithOptions = useMemo(() => {
    const customMapItemToFormData = (item: InvoicePayment): Partial<FormInvoicePayment> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId != null && baseData.clientId !== '' ? String(baseData.clientId) : '',
        invoiceId: baseData.invoiceId != null ? String(baseData.invoiceId) : '',
      };
    };

    const customGetInitialFormData = (): Partial<FormInvoicePayment> => ({
      ...getInitialFormData(),
      clientId: '',
      invoiceId: '',
    });

    const toApiPayload = (data: Partial<FormInvoicePayment>): Partial<InvoicePayment> => {
      const invoiceIdNum =
        data.invoiceId !== undefined && data.invoiceId !== null && String(data.invoiceId) !== ''
          ? Number(data.invoiceId)
          : undefined;
      return {
        paymentId: data.paymentId,
        paymentDate: data.paymentDate,
        paymentAmount: data.paymentAmount,
        paymentNotes: data.paymentNotes,
        paymentCreatedBy: data.paymentCreatedBy,
        paymentUpdatedBy: data.paymentUpdatedBy,
        invoiceId: invoiceIdNum,
      };
    };

    const customCreate = async (data: Partial<FormInvoicePayment>) => {
      return await moduleConfig.form.create(toApiPayload(data));
    };

    const customUpdate = async (id: number, data: Partial<FormInvoicePayment>) => {
      return await moduleConfig.form.update(id, toApiPayload(data));
    };

    return {
      ...moduleConfig,
      getInitialFormData: customGetInitialFormData as () => Partial<InvoicePayment>,
      mapItemToFormData: customMapItemToFormData as (item: InvoicePayment) => Partial<InvoicePayment>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<InvoicePayment>) => Promise<InvoicePayment>,
        update: customUpdate as (id: number, data: Partial<InvoicePayment>) => Promise<InvoicePayment>,
      },
    };
  }, []);

  return <GenericFormPage<InvoicePayment> useData={useInvoicePayment} config={configWithOptions} />;
};

export default Form;
